import { z } from 'zod';
import { MICROSOFT365_WRITE_TOOLS, microsoft365GraphId, type Microsoft365WriteTool } from './microsoft365.js';

/**
 * Microsoft 365 part 3: mail and meeting writes (D-220…, SA-112…). One locked card per message (R1): the worker
 * builds a canonical payload (JSON text, canonicalJson) from the model's words, Graph's header facts and the account,
 * stores it with its SHA-256 and a display-only view in `approvals.request.microsoft365` (immutable for the app role),
 * and parks the run. Nothing is written to Outlook before the person's click: the browser echoes the hash
 * (POST /api/microsoft365/approvals/:id/send), the API applies the person's edits, rebuilds the canonical text and
 * hands it to the gateway (POST /v1/microsoft365/write, the API's token only), and a database function turns the
 * pending approval into approved for exactly that hash, once. No write is ever retried: an answer that never came
 * ends as `unknown`.
 *
 * Every schema here validates without transforming (the text that was hashed is the text that is executed); a
 * payload is also in canonical form (isCanonicalJson). Hashing stays on the server (no Node crypto here: the web and
 * the phone use this file).
 */

export const MICROSOFT365_WRITE_LIMITS = {
  /** To and Cc of one mail together, at least one (no Bcc, no attachments) */
  recipients: 25,
  /** attendees of one meeting besides the organizer, none for a meeting with oneself (free/busy takes 20 calendars with the organizer's) */
  attendees: 19,
  subject: 255,
  body: 20_000,
  note: 4_000,
  /** a meeting's length in minutes */
  minMinutes: 15,
  maxMinutes: 480,
  /** the canonical payload text, in characters */
  payload: 60_000,
  /** the view text, in characters */
  view: 200_000,
  /** how long a card waits by default (MS365_WRITE_CARD_TTL_SECONDS); a meeting card also expires a minute before it starts */
  cardTtlSeconds: 1_800,
  meetingLeadSeconds: 60,
} as const;

export type Microsoft365WriteKind = 'mail' | 'meeting';

/** The card each write tool asks with: MsSohbetOnay (mail) or MsToplantiOnay (meeting). */
export const MICROSOFT365_WRITE_KIND: Record<Microsoft365WriteTool, Microsoft365WriteKind> = {
  yanit_taslagi: 'mail',
  taslagi_gonder: 'mail',
  mail_gonder: 'mail',
  toplanti_olustur: 'meeting',
};

export type Microsoft365MailMode = 'reply' | 'reply_all' | 'new' | 'draft';

/** The mail modes each mail tool may build: a reply (to the sender or to all), an existing Outlook draft, a new mail. */
export const MICROSOFT365_MAIL_MODES: Record<Exclude<Microsoft365WriteTool, 'toplanti_olustur'>, readonly Microsoft365MailMode[]> = {
  yanit_taslagi: ['reply', 'reply_all'],
  taslagi_gonder: ['draft'],
  mail_gonder: ['new'],
};

/** SHA-256 of a payload text, in lower-case hex (computed on the server only). */
export const sha256Hex = z.string().regex(/^[0-9a-f]{64}$/, 'a SHA-256 in lower-case hex');

/** one line (names, addresses, subjects): no control character at all, CR and LF included */
// eslint-disable-next-line no-control-regex
const ONE_LINE = /^[^\u0000-\u001f\u007f]*$/;
/** a text of several lines as a payload keeps it: tabs and \n only (line ends are \n, never \r) */
// eslint-disable-next-line no-control-regex
const TEXT_BLOCK = /^[^\u0000-\u0008\u000b-\u001f\u007f]*$/;
/** a text as a person typed it in the card (\r\n allowed; normalizeLineEnds makes it \n before the payload is built) */
// eslint-disable-next-line no-control-regex
const TYPED_BLOCK = /^[^\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]*$/;

const oneLine = (max: number) => z.string().max(max).regex(ONE_LINE, 'no control characters (CR, LF…)');
const textBlock = (max: number) => z.string().max(max).regex(TEXT_BLOCK, 'line ends are \\n; no other control characters');
const typedBlock = (max: number) => z.string().max(max).regex(TYPED_BLOCK, 'no control characters but tabs and line ends');

/** Line ends as a payload keeps them: \r\n and a lone \r become \n (nothing else changes). */
export const normalizeLineEnds = (text: string): string => text.replace(/\r\n?/g, '\n');

/** An e-mail address as Graph takes it (one line, at most 320 characters). */
export const microsoft365Address = z.email().max(320);

/** A recipient or an attendee: a display name (may be empty for a typed address) and the address. */
export const microsoft365Recipient = z.object({ name: oneLine(256), address: microsoft365Address }).strict();
export type Microsoft365Recipient = z.infer<typeof microsoft365Recipient>;

/** The domain of an address, lower case ("Ayse.Demir@Contoso.com" → "contoso.com"). */
export const addressDomain = (address: string): string => address.slice(address.lastIndexOf('@') + 1).toLowerCase();

const uniqueAddresses = (people: readonly { address: string }[]): boolean => new Set(people.map((p) => p.address.toLowerCase())).size === people.length;

/** An IANA time zone the runtime knows ("Europe/Istanbul"); checked, never changed. */
const ianaZone = z
  .string()
  .min(1)
  .max(64)
  .refine((value) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: value });
      return true;
    } catch {
      return false;
    }
  }, 'unknown time zone');

// ---- the canonical payloads (what is sent, exactly) -----------------------------------------------------

/** The mail a reply answers, or the Outlook draft that is sent (null for a new mail). */
export const microsoft365MailSource = z
  .object({
    /** its ref in the conversation (m3) */
    ref: z.string().regex(/^m[1-9]\d{0,4}$/),
    /** Graph's immutable id */
    messageId: microsoft365GraphId,
    /** a draft: Graph's changeKey when the card was made; the gateway refuses to send a draft that changed since */
    changeKey: oneLine(512).min(1).optional(),
  })
  .strict();

export const microsoft365MailPayload = z
  .object({
    v: z.literal(1),
    kind: z.literal('mail'),
    mode: z.enum(['reply', 'reply_all', 'new', 'draft']),
    source: microsoft365MailSource.nullable(),
    to: z.array(microsoft365Recipient).min(1).max(MICROSOFT365_WRITE_LIMITS.recipients),
    cc: z.array(microsoft365Recipient).max(MICROSOFT365_WRITE_LIMITS.recipients),
    subject: oneLine(MICROSOFT365_WRITE_LIMITS.subject),
    /** plain text; the gateway sends it as the mail's text (a reply puts Outlook's own quote below it) */
    body: textBlock(MICROSOFT365_WRITE_LIMITS.body),
  })
  .strict()
  .refine((p) => (p.mode === 'new') === (p.source === null), { path: ['source'], message: 'a new mail has no source; a reply or a draft has one' })
  .refine((p) => p.mode !== 'draft' || Boolean(p.source?.changeKey), { path: ['source', 'changeKey'], message: 'a draft is sent only with the changeKey the card showed' })
  .refine((p) => p.to.length + p.cc.length <= MICROSOFT365_WRITE_LIMITS.recipients, { path: ['cc'], message: `at most ${MICROSOFT365_WRITE_LIMITS.recipients} recipients (To and Cc)` })
  .refine((p) => uniqueAddresses([...p.to, ...p.cc]), { path: ['to'], message: 'each address once (To and Cc)' });
export type Microsoft365MailPayload = z.infer<typeof microsoft365MailPayload>;

const minutesBetween = (start: string, end: string): number => (Date.parse(end) - Date.parse(start)) / 60_000;

export const microsoft365MeetingPayload = z
  .object({
    v: z.literal(1),
    kind: z.literal('meeting'),
    subject: oneLine(MICROSOFT365_WRITE_LIMITS.subject).min(1),
    /** UTC instants ("2026-10-31T12:30:00.000Z") */
    start: z.iso.datetime(),
    end: z.iso.datetime(),
    /** the zone the time was chosen in; Graph gets the times in it */
    timeZone: ianaZone,
    /**
     * the other people; the organizer is the account and is not listed. Empty: a meeting with oneself (a calendar entry
     * with its Teams link; no invitation goes out)
     */
    attendees: z.array(microsoft365Recipient).max(MICROSOFT365_WRITE_LIMITS.attendees),
    /** the meeting's note (plain text, may be empty) */
    note: textBlock(MICROSOFT365_WRITE_LIMITS.note),
    /** a Teams meeting (isOnlineMeeting with teamsForBusiness); false when the account has no Teams */
    online: z.boolean(),
    /** Graph's idempotency key for the event (the approval's id); metadata only, never a reason to retry */
    transactionId: z.uuid(),
  })
  .strict()
  .refine((p) => minutesBetween(p.start, p.end) >= MICROSOFT365_WRITE_LIMITS.minMinutes && minutesBetween(p.start, p.end) <= MICROSOFT365_WRITE_LIMITS.maxMinutes, {
    path: ['end'],
    message: `a meeting lasts ${MICROSOFT365_WRITE_LIMITS.minMinutes} to ${MICROSOFT365_WRITE_LIMITS.maxMinutes} minutes`,
  })
  .refine((p) => uniqueAddresses(p.attendees), { path: ['attendees'], message: 'each address once' });
export type Microsoft365MeetingPayload = z.infer<typeof microsoft365MeetingPayload>;

export const microsoft365WritePayload = z.discriminatedUnion('kind', [microsoft365MailPayload, microsoft365MeetingPayload]);
export type Microsoft365WritePayload = z.infer<typeof microsoft365WritePayload>;

/** Everyone a payload writes to: To and Cc, or the attendees (pilot guard, counts, recipient provenance). */
export function microsoft365Recipients(payload: Microsoft365WritePayload): Microsoft365Recipient[] {
  return payload.kind === 'mail' ? [...payload.to, ...payload.cc] : [...payload.attendees];
}

// ---- the card: approvals.request.microsoft365 -----------------------------------------------------------

/**
 * What a Microsoft write card carries in `approvals.request.microsoft365` (and `approval.requested.microsoft365`).
 * Strings survive the client's camel-case transform of jsonb keys: `payload` and `view` are JSON text. The request
 * has no `sent` (old phone builds show the generic card, whose "allow once" the API refuses: microsoft_card_required).
 */
export const microsoft365WriteCard = z
  .object({
    v: z.literal(1),
    tool: z.enum(MICROSOFT365_WRITE_TOOLS),
    kind: z.enum(['mail', 'meeting']),
    /** the canonical payload text as the worker built it (before any edit) */
    payload: z.string().min(2).max(MICROSOFT365_WRITE_LIMITS.payload),
    /** SHA-256 of `payload`: what the browser echoes */
    hash: sha256Hex,
    /** display-only facts (Microsoft365WriteView) as JSON text */
    view: z.string().min(2).max(MICROSOFT365_WRITE_LIMITS.view),
  })
  .strict()
  .refine((card) => MICROSOFT365_WRITE_KIND[card.tool] === card.kind, { path: ['kind'], message: "the tool's card kind" });
export type Microsoft365WriteCard = z.infer<typeof microsoft365WriteCard>;

// ---- the view: what the card shows besides the payload (display only, never sent) ------------------------

const shownPerson = z.object({ name: z.string().max(256), address: z.string().max(320) });
const shownAttachment = z.object({ name: z.string().max(300), size: z.number().int().nonnegative(), contentType: z.string().max(200) });

/** MsSohbetOnay: Kimden, the recipients' notes, the answered mail's row and text (a draft's facts for taslagi_gonder). */
export const microsoft365MailView = z.object({
  v: z.literal(1),
  kind: z.literal('mail'),
  /** Kimden: the connected account (Graph sends from the mailbox owner) */
  from: shownPerson,
  /** per address of the payload's To and Cc: outside the account's domain ("Şirket dışı"); came from the answered mail's Reply-To */
  flags: z.array(z.object({ address: z.string().max(320), external: z.boolean(), replyTo: z.boolean() })).max(50),
  /** the answered mail (reply, reply_all) or the draft (draft) as Graph said it; null for a new mail */
  source: z
    .object({
      from: shownPerson,
      /** a Reply-To that differs from the sender's domain: "Yanıt, gönderenin değil, … adresine gider" */
      replyTo: shownPerson.nullable(),
      subject: z.string().max(500),
      receivedAt: z.string().max(40),
      isRead: z.boolean(),
      /** the answered mail's attachments; for a draft, the attachments that go with it */
      attachments: z.array(shownAttachment).max(50),
      /** the model's one-line summary on the mail row (ozet) */
      summary: z.string().max(300).nullable(),
      /** "Yanıtlanan mail": its text, plain and shortened (empty for a draft) */
      text: z.string().max(MICROSOFT365_WRITE_LIMITS.body),
    })
    .nullable(),
});

/** MsToplantiOnay: the organizer, Teams, the attendees' notes and the free/busy strip. */
export const microsoft365MeetingView = z.object({
  v: z.literal(1),
  kind: z.literal('meeting'),
  /** "düzenleyen": the connected account */
  organizer: shownPerson,
  /** the account's calendar offers Teams meetings; false: the card says the meeting has no Teams link */
  teams: z.boolean(),
  /** per attendee address: outside the account's domain */
  flags: z.array(z.object({ address: z.string().max(320), external: z.boolean() })).max(40),
  /**
   * free/busy over the strip's window, so a chosen alternative or an edited time recolours the chips without a call;
   * null: the calendars were not read (the card says so)
   */
  schedule: z
    .object({
      start: z.iso.datetime(),
      end: z.iso.datetime(),
      /** the cells' length in minutes (30) */
      interval: z.number().int().min(5).max(240),
      /** everyone's busy stretches, the organizer first; busy null: that calendar could not be seen */
      people: z
        .array(z.object({ address: z.string().max(320), busy: z.array(z.object({ start: z.iso.datetime(), end: z.iso.datetime() })).max(200).nullable() }))
        .max(MICROSOFT365_WRITE_LIMITS.attendees + 1),
      /** "Başka saat seç": up to two later starts when everyone is free */
      alternatives: z.array(z.iso.datetime()).max(2),
    })
    .nullable(),
});

export const microsoft365WriteView = z.discriminatedUnion('kind', [microsoft365MailView, microsoft365MeetingView]);
export type Microsoft365MailView = z.infer<typeof microsoft365MailView>;
export type Microsoft365MeetingView = z.infer<typeof microsoft365MeetingView>;
export type Microsoft365WriteView = z.infer<typeof microsoft365WriteView>;

// ---- Düzenle and Gönder: POST /api/microsoft365/approvals/:id/send ----------------------------------------

/** Mail fields the person changed in the card; Kimden and the answered mail cannot change. */
export const microsoft365MailEdits = z
  .object({
    kind: z.literal('mail'),
    to: z.array(microsoft365Recipient).min(1).max(MICROSOFT365_WRITE_LIMITS.recipients).optional(),
    cc: z.array(microsoft365Recipient).max(MICROSOFT365_WRITE_LIMITS.recipients).optional(),
    subject: oneLine(MICROSOFT365_WRITE_LIMITS.subject).optional(),
    body: typedBlock(MICROSOFT365_WRITE_LIMITS.body).optional(),
  })
  .strict();

/** Meeting fields the person changed ("Başka saat seç" is a time edit); the day and the start are in the payload's zone. */
export const microsoft365MeetingEdits = z
  .object({
    kind: z.literal('meeting'),
    subject: oneLine(MICROSOFT365_WRITE_LIMITS.subject).min(1).optional(),
    date: z.iso.date().optional(),
    start: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM')
      .optional(),
    durationMinutes: z.number().int().min(MICROSOFT365_WRITE_LIMITS.minMinutes).max(MICROSOFT365_WRITE_LIMITS.maxMinutes).optional(),
    /** empty: a meeting with oneself (no invitation goes out) */
    attendees: z.array(microsoft365Recipient).max(MICROSOFT365_WRITE_LIMITS.attendees).optional(),
    note: typedBlock(MICROSOFT365_WRITE_LIMITS.note).optional(),
  })
  .strict();

export const microsoft365WriteEdits = z.discriminatedUnion('kind', [microsoft365MailEdits, microsoft365MeetingEdits]);
export type Microsoft365MailEdits = z.infer<typeof microsoft365MailEdits>;
export type Microsoft365MeetingEdits = z.infer<typeof microsoft365MeetingEdits>;
export type Microsoft365WriteEdits = z.infer<typeof microsoft365WriteEdits>;

/** "Gönder" / "Oluştur ve davet et": the hash of the text the browser rendered, and the fields it changed. */
export const microsoft365SendBody = z
  .object({
    hash: sha256Hex,
    edits: microsoft365WriteEdits.optional(),
  })
  .strict();
export type Microsoft365SendBody = z.infer<typeof microsoft365SendBody>;

// ---- outcomes -------------------------------------------------------------------------------------------

/**
 * How a write card ended: sent / created (Outlook has it), failed (Microsoft refused or a check stopped it; nothing
 * more is tried), unknown (Microsoft's answer never came: "Gönderilip gönderilmediği bilinmiyor; Outlook'ta Gönderilmiş
 * Öğeler'e bakın"), expired, cancelled ("Vazgeç"), stopped ("Durdur").
 */
export const MICROSOFT365_WRITE_OUTCOMES = ['sent', 'created', 'failed', 'unknown', 'expired', 'cancelled', 'stopped'] as const;
export type Microsoft365WriteOutcomeName = (typeof MICROSOFT365_WRITE_OUTCOMES)[number];

/** The `approval.resolved` status each outcome comes with. */
export const MICROSOFT365_OUTCOME_STATUS: Record<Microsoft365WriteOutcomeName, 'approved' | 'rejected' | 'expired' | 'cancelled'> = {
  sent: 'approved',
  created: 'approved',
  failed: 'approved',
  unknown: 'approved',
  expired: 'expired',
  cancelled: 'rejected',
  stopped: 'cancelled',
};

/**
 * Codes an outcome may carry (open list; codes only, never Microsoft's text). failed: the draft changed in Outlook
 * since the card (draft_changed), Outlook's copy differs from the card (verify_mismatch, the draft stays in Taslaklar),
 * Microsoft busy, refused, missing or failing; unknown: timeout.
 */
export const MICROSOFT365_WRITE_FAILURE_CODES = [
  'draft_changed',
  'verify_mismatch',
  'microsoft_busy',
  'microsoft_forbidden',
  'microsoft_not_found',
  'microsoft_rejected',
  'microsoft_failed',
  'timeout',
] as const;

/** A card's end, folded into `approval.resolved.microsoft365` (the resolved card shows the sent version). */
export const microsoft365WriteOutcome = z.object({
  outcome: z.enum(MICROSOFT365_WRITE_OUTCOMES),
  code: z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,59}$/)
    .optional(),
  /** created: the meeting has its Teams link */
  online: z.boolean().optional(),
  at: z.iso.datetime(),
  /** what went to the gateway (after the person's edits) and its hash; absent for expired, cancelled and stopped */
  payload: z.string().max(MICROSOFT365_WRITE_LIMITS.payload).optional(),
  hash: sha256Hex.optional(),
});
export type Microsoft365WriteOutcome = z.infer<typeof microsoft365WriteOutcome>;

/** The send route's answer (200): the outcome after the gateway; a refusal before anything was written is an error instead. */
export interface Microsoft365SendResultDto {
  outcome: 'sent' | 'created' | 'failed' | 'unknown';
  code?: string;
  online?: boolean;
  /** the text that went to the gateway and its hash: the resolved card shows this version */
  payload: string;
  hash: string;
  at: string;
}

// ---- the gateway's write route (internal: POST /v1/microsoft365/write, the API's token only) --------------

export const microsoft365GatewayWriteBody = z
  .object({
    approvalId: z.uuid(),
    /** the card's hash the browser rendered (approvals.request.microsoft365.hash) */
    shownHash: sha256Hex,
    /** the canonical payload text after the person's edits */
    payload: z.string().min(2).max(MICROSOFT365_WRITE_LIMITS.payload),
    payloadHash: sha256Hex,
    /** the person changed something: the payload's hash may differ from the card's */
    edited: z.boolean(),
    /** v0.15f.1 (ADR-0022): the API's grant for this click, bound to all of the above, the person and the account */
    grant: z.string().min(16).max(4096),
  })
  .strict();
export type Microsoft365GatewayWriteBody = z.infer<typeof microsoft365GatewayWriteBody>;

export interface Microsoft365GatewayWriteResult {
  outcome: 'sent' | 'created' | 'failed' | 'unknown';
  code?: string;
  online?: boolean;
}
